import React, { useState } from "react";
import { X } from "lucide-react";
import persona from "@/assets/images/persona-square.png";
import check from "@/assets/images/check.png";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import CommonSelect from "@/shared/components/ui/Select/CommonSelect";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import CommonDatePicker from "@/shared/components/ui/DatePicker/CommonDatePicker";

const DEAL_TYPE_OPTIONS = [
  { key: "New Business", label: "New Business" },
  { key: "Renewal", label: "Renewal" },
  { key: "Upsell", label: "Upsell" },
  { key: "Cross-sell", label: "Cross-sell" },
];

const STATUS_OPTIONS = [
  { key: "in_process", label: "In Process" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

const LEAD_SOURCE_OPTIONS = [
  { key: "Website", label: "Website" },
  { key: "Referral", label: "Referral" },
  { key: "Outbound", label: "Outbound" },
  { key: "Event", label: "Event" },
  { key: "Partner", label: "Partner" },
  { key: "Social", label: "Social" },
  { key: "Other", label: "Other" },
];

const CURRENCY_OPTIONS = [
  { key: "USD", label: "USD" },
  { key: "GBP", label: "GBP" },
  { key: "EUR", label: "EUR" },
  { key: "INR", label: "INR" },
  { key: "AUD", label: "AUD" },
  { key: "CAD", label: "CAD" },
];

const COMPANY_SIZE_OPTIONS = [
  { key: "1 employee", label: "1 employee" },
  { key: "2-49 employees", label: "2-49 employees" },
  { key: "50-249 employees", label: "50-249 employees" },
  { key: "250-999 employees", label: "250-999 employees" },
  { key: "1000+ employees", label: "1000+ employees" },
];

const FORECAST_CATEGORY_OPTIONS = [
  { key: "pipeline", label: "Pipeline" },
  { key: "best_case", label: "Best Case" },
  { key: "committed", label: "Committed" },
  { key: "omitted", label: "Omitted" },
];

const formatAmount = (amount, currency) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(amount || 0);

const Field = ({ label, children, required }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
      {label}
      {required && <span className="text-secondary ml-0.5">*</span>}
    </span>
    {children}
  </div>
);

const CreateDeal = ({ companyName, deal, isSaving, onSubmit, onClose }) => {
  const [name, setName] = useState(deal?.name ?? "");
  const [amount, setAmount] = useState(deal?.amount ?? "");
  const [probability, setProbability] = useState(deal?.probability ?? 0);
  const [currency, setCurrency] = useState(deal?.currency || "USD");
  const [status, setStatus] = useState(deal?.status || "in_process");
  const [dealType, setDealType] = useState(deal?.dealType || "");
  const [leadSource, setLeadSource] = useState(deal?.leadSource || "");
  const [companySize, setCompanySize] = useState(deal?.companySize || "");
  const [forecastCategory, setForecastCategory] = useState(
    deal?.forecastCategory || "pipeline",
  );
  const [pipeline, setPipeline] = useState(deal?.pipeline ?? "");
  const [nextStep, setNextStep] = useState(deal?.nextStep ?? "");
  const [campaignSource, setCampaignSource] = useState(
    deal?.campaignSource ?? "",
  );
  const [contactName, setContactName] = useState(deal?.contactName ?? "");
  const [industry, setIndustry] = useState(deal?.industry ?? "");
  const [socialLeadId, setSocialLeadId] = useState(deal?.socialLeadId ?? "");
  const [estimatedCloseDate, setEstimatedCloseDate] = useState(
    deal?.estimatedCloseDate?.slice(0, 10) ?? "",
  );

  const expectedRevenue = (Number(amount) || 0) * ((Number(probability) || 0) / 100);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      name,
      amount,
      pipeline,
      status,
      dealType: dealType || null,
      nextStep: nextStep || null,
      leadSource: leadSource || null,
      contactName: contactName || null,
      currency,
      socialLeadId: socialLeadId || null,
      probability,
      campaignSource: campaignSource || null,
      industry: industry || null,
      companySize: companySize || null,
      forecastCategory,
      estimatedCloseDate: estimatedCloseDate || null,
    });
  };

  return (
    <CustomModal
      isOpen
      width="900px"
      className="!p-0 !rounded-[16px] !gap-0 !items-stretch"
    >
      <form
        onSubmit={handleSubmit}
        className="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-[16px] bg-[var(--bg-standard)] font-sans"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 dark:border-gray-800 p-5">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">
              {deal ? "Edit Deal" : "Create Deal"}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {deal
                ? "Update the complete deal information below."
                : "Add the information needed to track this opportunity."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid flex-1 grid-cols-1 gap-5 overflow-auto p-5 md:grid-cols-2">
          {/* Deal Name */}
          <Field label="Deal Name" required>
            <CommonInput
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Enter deal name"
              wrapperClassName="min-w-0 w-full"
            />
          </Field>

          {/* Deal Amount */}
          <Field label="Deal Amount" required>
            <CommonInput
              name="amount"
              type="text"
              inputMode="decimal"
              value={String(amount)}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*\.?\d*$/.test(val)) setAmount(val);
              }}
              onKeyDown={(e) => {
                const allowed = /[0-9.]/.test(e.key);
                const control = e.ctrlKey || e.metaKey;
                const nav = ["Backspace","Delete","ArrowLeft","ArrowRight","Tab","Enter"].includes(e.key);
                // Block second decimal point
                if (e.key === "." && String(amount).includes(".")) { e.preventDefault(); return; }
                if (!allowed && !control && !nav) e.preventDefault();
              }}
              required
              placeholder="Enter deal amount"
              wrapperClassName="min-w-0 w-full"
              inputClassName="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </Field>

          {/* Associated Company (read-only) */}
          <Field label="Associated Company">
            <div className="flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2">
              <img src={persona} alt="" className="h-6 w-6 rounded-full" />
              <span className="text-sm text-gray-700 dark:text-gray-200 flex-1">{companyName}</span>
              <img src={check} alt="Selected" className="h-4 w-4" />
            </div>
          </Field>

          {/* Estimated Closing Date */}
          <Field label="Estimated Closing Date">
            <CommonDatePicker
              value={estimatedCloseDate || null}
              onChange={setEstimatedCloseDate}
              placeholder="Select closing date"
              width="w-full"
            />
          </Field>

          {/* Deal Type */}
          <Field label="Deal Type">
            <CommonSelect
              value={dealType}
              onChange={setDealType}
              options={DEAL_TYPE_OPTIONS}
              placeholder="Select type"
              width="w-full"
            />
          </Field>

          {/* Stage / Status */}
          <Field label="Stage / Status" required>
            <CommonSelect
              value={status}
              onChange={setStatus}
              options={STATUS_OPTIONS}
              placeholder="Select status"
              width="w-full"
            />
          </Field>

          {/* Pipeline Category */}
          <Field label="Pipeline Category" required>
            <CommonInput
              name="pipeline"
              value={pipeline}
              onChange={(e) => setPipeline(e.target.value)}
              required
              placeholder="e.g. Enterprise Sales"
              wrapperClassName="min-w-0 w-full"
            />
          </Field>

          {/* Probability */}
          <Field label="Probability (%)">
            <CommonInput
              name="probability"
              type="text"
              inputMode="numeric"
              value={String(probability)}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*$/.test(val)) {
                  const num = val === "" ? 0 : Math.min(100, Number(val));
                  setProbability(num);
                }
              }}
              onKeyDown={(e) => {
                const allowed = /[0-9]/.test(e.key);
                const control = e.ctrlKey || e.metaKey;
                const nav = ["Backspace","Delete","ArrowLeft","ArrowRight","Tab","Enter"].includes(e.key);
                if (!allowed && !control && !nav) e.preventDefault();
              }}
              placeholder="0–100"
              wrapperClassName="min-w-0 w-full"
              inputClassName="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </Field>

          {/* Next Step */}
          <Field label="Next Step">
            <CommonInput
              name="nextStep"
              value={nextStep}
              onChange={(e) => setNextStep(e.target.value)}
              placeholder="e.g. Schedule proposal review"
              wrapperClassName="min-w-0 w-full"
            />
          </Field>

          {/* Expected Revenue (computed, read-only) */}
          <Field label="Expected Revenue">
            <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 font-medium">
              {formatAmount(expectedRevenue, currency)}
            </div>
          </Field>

          {/* Lead Source */}
          <Field label="Lead Source">
            <CommonSelect
              value={leadSource}
              onChange={setLeadSource}
              options={LEAD_SOURCE_OPTIONS}
              placeholder="Select lead source"
              width="w-full"
            />
          </Field>

          {/* Campaign Source */}
          <Field label="Campaign Source">
            <CommonInput
              name="campaignSource"
              value={campaignSource}
              onChange={(e) => setCampaignSource(e.target.value)}
              placeholder="Enter campaign source"
              wrapperClassName="min-w-0 w-full"
            />
          </Field>

          {/* Contact Name */}
          <Field label="Contact Name">
            <CommonInput
              name="contactName"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Enter contact name"
              wrapperClassName="min-w-0 w-full"
            />
          </Field>

          {/* Industry */}
          <Field label="Industry">
            <CommonInput
              name="industry"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="e.g. IT Services"
              wrapperClassName="min-w-0 w-full"
            />
          </Field>

          {/* Currency */}
          <Field label="Currency">
            <CommonSelect
              value={currency}
              onChange={setCurrency}
              options={CURRENCY_OPTIONS}
              placeholder="Select currency"
              width="w-full"
            />
          </Field>

          {/* Company Size */}
          <Field label="Company Size">
            <CommonSelect
              value={companySize}
              onChange={setCompanySize}
              options={COMPANY_SIZE_OPTIONS}
              placeholder="Select company size"
              width="w-full"
            />
          </Field>

          {/* Social Lead ID */}
          <Field label="Social Lead ID">
            <CommonInput
              name="socialLeadId"
              value={socialLeadId}
              onChange={(e) => setSocialLeadId(e.target.value)}
              placeholder="Enter social lead ID"
              wrapperClassName="min-w-0 w-full"
            />
          </Field>

          {/* Forecast Category */}
          <Field label="Forecast Category">
            <CommonSelect
              value={forecastCategory}
              onChange={setForecastCategory}
              options={FORECAST_CATEGORY_OPTIONS}
              placeholder="Select forecast category"
              width="w-full"
            />
          </Field>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-800 p-4">
          <SecondaryButton title="Cancel" onClick={onClose} disabled={isSaving} />
          <PrimaryButton
            type="submit"
            startContent={false}
            isLoading={isSaving}
            disabled={isSaving}
          >
            {deal ? "Save Changes" : "Save and Create"}
          </PrimaryButton>
        </div>
      </form>
    </CustomModal>
  );
};

export default CreateDeal;
